import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from 'firebase/auth';
import { AuthContext, type AuthContextValue } from '@/app/contexts/authContext';
import { isFirebaseConfigured } from '@/config/env';
import { hasPermission as checkPermission } from '@/config/permissions';
import {
  ensureUserProfile,
  signIn as authSignIn,
  signInWithGoogle as authSignInWithGoogle,
  signOutUser,
  subscribeToAuthState,
} from '@/services/auth';
import { logLogin, logLogout, setAuditActor } from '@/services/audit';
import { requireFirebaseConfigured } from '@/services/firebase';
import { isUserApproved, type UserProfile } from '@/types/user';

const LOGIN_AUDIT_SESSION_KEY = 'erp:audit:login-logged';

type AuthProviderProps = {
  children: ReactNode;
};

const disabledAuthValue: AuthContextValue = {
  firebaseUser: null,
  profile: null,
  loading: false,
  profileError: null,
  isAuthenticated: false,
  signIn: async () => {
    throw new Error('Firebase no configurado.');
  },
  signInWithGoogle: async () => {
    throw new Error('Firebase no configurado.');
  },
  signOut: async () => undefined,
  hasPermission: () => false,
  refreshProfile: async () => undefined,
};

export function AuthProvider({ children }: AuthProviderProps) {
  const firebaseReady = isFirebaseConfigured();

  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(firebaseReady);
  const [profileError, setProfileError] = useState<string | null>(null);

  const loadProfile = useCallback(async (user: User) => {
    setProfileError(null);
    try {
      const doc = await ensureUserProfile(user);
      if (!doc) {
        setProfile(null);
        setProfileError(
          'Tu cuenta no tiene perfil en el sistema. Usá Google o pedí acceso al superadmin.',
        );
        return;
      }
      if (!doc.active && doc.role !== 'pending') {
        await signOutUser();
        setProfile(null);
        setProfileError('Tu cuenta está deshabilitada.');
        return;
      }
      setProfile(doc);
    } catch {
      setProfile(null);
      setProfileError('Error al cargar o crear el perfil de usuario.');
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!firebaseUser) return;
    await loadProfile(firebaseUser);
  }, [firebaseUser, loadProfile]);

  useEffect(() => {
    if (profile && firebaseUser && isUserApproved(profile)) {
      setAuditActor({
        uid: firebaseUser.uid,
        email: profile.email,
        role: profile.role,
        companyId: profile.companyId,
      });
      if (
        typeof sessionStorage !== 'undefined' &&
        !sessionStorage.getItem(LOGIN_AUDIT_SESSION_KEY)
      ) {
        sessionStorage.setItem(LOGIN_AUDIT_SESSION_KEY, '1');
        void logLogin();
      }
    } else {
      setAuditActor(null);
    }
  }, [profile, firebaseUser]);

  useEffect(() => {
    if (!firebaseReady) return;

    const unsubscribe = subscribeToAuthState(async (user) => {
      setFirebaseUser(user);
      if (!user) {
        setProfile(null);
        setProfileError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      await loadProfile(user);
      setLoading(false);
    });
    return unsubscribe;
  }, [firebaseReady, loadProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    requireFirebaseConfigured();
    setProfileError(null);
    await authSignIn(email, password);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    requireFirebaseConfigured();
    setProfileError(null);
    await authSignInWithGoogle();
  }, []);

  const signOut = useCallback(async () => {
    if (!firebaseReady) return;
    await logLogout();
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(LOGIN_AUDIT_SESSION_KEY);
    }
    setAuditActor(null);
    setProfile(null);
    setProfileError(null);
    await signOutUser();
  }, [firebaseReady]);

  const hasPermission = useCallback(
    (permission: Parameters<AuthContextValue['hasPermission']>[0]) =>
      checkPermission(profile?.role, permission),
    [profile?.role],
  );

  const isAuthenticated = Boolean(
    firebaseUser && profile && isUserApproved(profile),
  );

  const value = useMemo<AuthContextValue>(() => {
    if (!firebaseReady) return disabledAuthValue;
    return {
      firebaseUser,
      profile,
      loading,
      profileError,
      isAuthenticated,
      signIn,
      signInWithGoogle,
      signOut,
      hasPermission,
      refreshProfile,
    };
  }, [
    firebaseReady,
    firebaseUser,
    profile,
    loading,
    profileError,
    isAuthenticated,
    signIn,
    signInWithGoogle,
    signOut,
    hasPermission,
    refreshProfile,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
